import type { ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import capitalize from "lodash/capitalize";
import type { PlanStatus, SWPPlan } from "creso-core/types";
import { AmcLogo } from "../../logos";
import { PlanStatusBadge } from "../../badges";

export type SwpColumnOptions = {
  /** Row action menu. Omit it and no action column is produced. */
  renderActions?: (plan: SWPPlan) => ReactNode;
};

export function createSwpColumns({
  renderActions,
}: SwpColumnOptions = {}): ColumnDef<SWPPlan>[] {
  const columns: ColumnDef<SWPPlan>[] = [
    {
      header: "Scheme name",
      accessorKey: "scheme",
      minSize: 250,
      cell: (info) => (
        <div className="flex flex-row gap-2 items-center">
          <AmcLogo amc_code={info.row.original.amc_morningstar_code} />
          <span className="text-sm">{info.row.original.scheme_name}</span>
        </div>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: (info) => (
        <PlanStatusBadge status={info.getValue() as PlanStatus} />
      ),
    },
    {
      header: "Next Installment",
      accessorKey: "installment_due_date",
      cell: (info) => {
        if (!info.getValue()) return "-";
        return <span>{format(info.getValue() as string, "dd MMM yyyy")}</span>;
      },
    },
    {
      header: "Frequency",
      accessorKey: "frequency",
      cell: (info) => (
        <span className="text-sm">{capitalize(info.getValue() as string)}</span>
      ),
    },
  ];

  if (renderActions) {
    columns.push({
      id: "actions",
      cell: (info) => (
        <div className="text-right">{renderActions(info.row.original)}</div>
      ),
      minSize: 50,
      size: 50,
    });
  }

  return columns;
}
